import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, FileText, Clock, Eye, Download, Trash2, AlertCircle, RefreshCw, Search, Share2, Check } from 'lucide-react';
import { personalizedSummaryService, PersonalizedSummary } from '../lib/personalizedSummaryService';
import { useNavigate } from 'react-router-dom';
import { DigestPreviewDocket } from '../lib/watchlistDigestService';
import { teamWorkspaceService } from '../lib/teamWorkspaceService';
import { Workspace } from '../types';
import { formatFilingTypeLabel } from '../utils/formatFilingType';

interface PersonalizedSummaryDashboardProps {
  className?: string;
  /** Digest preview dockets passed from the Library page so we can show staleness badges
   *  without re-fetching inside this component. */
  digestDockets?: DigestPreviewDocket[];
}

export const PersonalizedSummaryDashboard: React.FC<PersonalizedSummaryDashboardProps> = ({
  className = '',
  digestDockets = [],
}) => {
  const navigate = useNavigate();
  const [personalizedSummaries, setPersonalizedSummaries] = useState<PersonalizedSummary[]>([]);
  const [filteredSummaries, setFilteredSummaries] = useState<PersonalizedSummary[]>([]);

  // Build a map of docket_number → new filing count from the digest preview
  const stalenessMap = new Map<string, number>(
    digestDockets
      .filter(d => d.new_filings_count > 0)
      .map(d => [d.docket_number, d.new_filings_count])
  );

  // Workspaces needed for the share popover
  const [workspaces, setWorkspaces] = useState<Array<Workspace & { user_role: string; access_type: string }>>([]);
  const [workspacesLoaded, setWorkspacesLoaded] = useState(false);

  // Share-popover state: which summary card has its popover open
  const [shareOpen, setShareOpen] = useState<string | null>(null);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [shareNotes, setShareNotes] = useState('');
  const [sharing, setSharing] = useState(false);
  const [shareSuccess, setShareSuccess] = useState<string | null>(null); // summary id that just succeeded
  const sharePopoverRef = useRef<HTMLDivElement>(null);

  // Close share popover on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sharePopoverRef.current && !sharePopoverRef.current.contains(e.target as Node)) {
        setShareOpen(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Lazily load workspaces the first time the user opens a share popover
  const ensureWorkspacesLoaded = async () => {
    if (workspacesLoaded) return;
    try {
      const ws = await teamWorkspaceService.getUserWorkspaces();
      setWorkspaces(ws);
    } catch {
      setWorkspaces([]);
    } finally {
      setWorkspacesLoaded(true);
    }
  };

  const openSharePopover = async (summaryId: string) => {
    setShareOpen(summaryId);
    setSelectedWorkspaceId('');
    setShareNotes('');
    setShareSuccess(null);
    await ensureWorkspacesLoaded();
  };

  const handleShare = async (summaryId: string) => {
    if (!selectedWorkspaceId) return;
    setSharing(true);
    try {
      await teamWorkspaceService.shareSummary({
        workspaceId: selectedWorkspaceId,
        summaryId,
        accessLevel: 'read',
        notes: shareNotes || undefined,
      });
      setShareSuccess(summaryId);
      setTimeout(() => {
        setShareOpen(null);
        setShareSuccess(null);
      }, 1500);
    } catch (err) {
      console.error('Share failed:', err);
    } finally {
      setSharing(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  
  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocketFilter, setSelectedDocketFilter] = useState('');
  const [selectedSummaryTypeFilter, setSelectedSummaryTypeFilter] = useState('');
  const [selectedFilingTypeFilter, setSelectedFilingTypeFilter] = useState('');

  useEffect(() => {
    loadAllPersonalizedSummaries();
  }, []);

  // Filter summaries when search term or filters change
  useEffect(() => {
    let filtered = personalizedSummaries;
    
    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(summary => 
        summary.docket_number.toLowerCase().includes(term) ||
        summary.topics.some(topic => topic.toLowerCase().includes(term)) ||
        summary.summary_type.toLowerCase().includes(term) ||
        (summary.filing_type && summary.filing_type.toLowerCase().includes(term))
      );
    }
    
    // Filter by docket number
    if (selectedDocketFilter) {
      filtered = filtered.filter(summary => summary.docket_number === selectedDocketFilter);
    }
    
    // Filter by summary type
    if (selectedSummaryTypeFilter) {
      filtered = filtered.filter(summary => summary.summary_type === selectedSummaryTypeFilter);
    }
    
    // Filter by filing type
    if (selectedFilingTypeFilter) {
      filtered = filtered.filter(summary => summary.filing_type === selectedFilingTypeFilter);
    }
    
    setFilteredSummaries(filtered);
  }, [personalizedSummaries, searchTerm, selectedDocketFilter, selectedSummaryTypeFilter, selectedFilingTypeFilter]);

  const loadAllPersonalizedSummaries = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Get all personalized summaries for the current user
      const summaries = await personalizedSummaryService.getAllUserPersonalizedSummaries();
      setPersonalizedSummaries(summaries);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load personalized summaries');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSummary = async (summaryId: string) => {
    if (!confirm('Are you sure you want to delete this personalized summary? This action cannot be undone.')) {
      return;
    }

    try {
      await personalizedSummaryService.deletePersonalizedSummary(summaryId);
      await loadAllPersonalizedSummaries(); // Reload the list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete summary');
    }
  };

  const handleViewSummary = async (summary: PersonalizedSummary) => {
    // Open the shareable view page for this summary. Each summary has a
    // share_token (backfilled by migration 20260515010000) so this works
    // for both new and old rows. Falls back to the proceedings page if
    // the token isn't present for some reason.
    if (summary.share_token) {
      navigate(`/s/${summary.share_token}`);
    } else {
      navigate(`/proceedings/${encodeURIComponent(summary.docket_number)}`);
    }
  };

  const handleDownloadSummary = async (summary: PersonalizedSummary) => {
    try {
      await personalizedSummaryService.downloadSummaryContent(summary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download summary');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTopics = (topics: string[]) => {
    return topics.join(', ');
  };

  const getSummaryTypeColor = (summaryType: string) => {
    switch (summaryType) {
      case 'multi-topic':
        return 'bg-blue-100 text-blue-800';
      case 'single-topic':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getFilingTypeColor = (filingType?: string) => {
    switch (filingType) {
      case 'COMMENT':
        return 'bg-purple-100 text-purple-800';
      case 'REPLY':
        return 'bg-orange-100 text-orange-800';
      case 'PETITION':
        return 'bg-red-100 text-red-800';
      case 'EXPRESS-COMMENT':
        return 'bg-orange-100 text-orange-700 border border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get unique values for filter dropdowns
  const getUniqueDockets = () => {
    const dockets = [...new Set(personalizedSummaries.map(summary => summary.docket_number))];
    return dockets.sort();
  };

  const getUniqueSummaryTypes = () => {
    const types = [...new Set(personalizedSummaries.map(summary => summary.summary_type))];
    return types.sort();
  };

  const getUniqueFilingTypes = () => {
    const types = [...new Set(personalizedSummaries.map(summary => summary.filing_type).filter(Boolean))];
    return types.sort();
  };

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedDocketFilter('');
    setSelectedSummaryTypeFilter('');
    setSelectedFilingTypeFilter('');
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-20 ${className}`}>
        <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`rounded-xl border border-red-200 bg-red-50 p-5 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-red-500" />
          <div>
            <p className="text-sm font-medium text-red-800">Failed to load summaries</p>
            <p className="mt-0.5 text-xs text-red-600">{error}</p>
            <button
              onClick={loadAllPersonalizedSummaries}
              className="mt-2 text-xs text-red-600 underline underline-offset-2 hover:text-red-800"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>

      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        {/* Left: count */}
        <p className="text-sm text-gray-500">
          {personalizedSummaries.length === 0
            ? 'No summaries yet'
            : `${filteredSummaries.length} of ${personalizedSummaries.length} summar${personalizedSummaries.length !== 1 ? 'ies' : 'y'}`}
        </p>

        {/* Right: search + filters + actions */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          {personalizedSummaries.length > 0 && (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                placeholder="Search docket, topics…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-8 rounded-lg border border-gray-200 bg-white pl-8 pr-3 text-xs text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300 w-48"
              />
            </div>
          )}

          {/* Filters */}
          {personalizedSummaries.length > 0 && (
            <>
              <select
                value={selectedDocketFilter}
                onChange={(e) => setSelectedDocketFilter(e.target.value)}
                className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
              >
                <option value="">All dockets</option>
                {getUniqueDockets().map(d => <option key={d} value={d}>{d}</option>)}
              </select>

              <select
                value={selectedSummaryTypeFilter}
                onChange={(e) => setSelectedSummaryTypeFilter(e.target.value)}
                className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
              >
                <option value="">All types</option>
                {getUniqueSummaryTypes().map(t => <option key={t} value={t}>{t.replace('-', ' ')}</option>)}
              </select>

              <select
                value={selectedFilingTypeFilter}
                onChange={(e) => setSelectedFilingTypeFilter(e.target.value)}
                className="h-8 rounded-lg border border-gray-200 bg-white px-2 text-xs text-gray-700 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
              >
                <option value="">All filing types</option>
                {getUniqueFilingTypes().map(t => <option key={t} value={t}>{t}</option>)}
              </select>

              {(searchTerm || selectedDocketFilter || selectedSummaryTypeFilter || selectedFilingTypeFilter) && (
                <button
                  onClick={resetFilters}
                  className="h-8 rounded-lg border border-gray-200 bg-white px-2.5 text-xs text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                >
                  Reset
                </button>
              )}
            </>
          )}

          {/* Refresh */}
          <button
            onClick={loadAllPersonalizedSummaries}
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* ── Empty states ─────────────────────────────────────────────────── */}
      {personalizedSummaries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100 mb-4">
            <Sparkles className="h-7 w-7 text-gray-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-700 mb-1">No summaries yet</h3>
          <p className="text-sm text-gray-500 max-w-xs mb-5">
            Generate your first personalized summary by analyzing a docket with your topics of interest.
          </p>
          <button
            onClick={() => navigate('/proceedings')}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
          >
            Browse proceedings
          </button>
        </div>
      ) : filteredSummaries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gray-100 mb-3">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <h3 className="text-sm font-semibold text-gray-700 mb-1">No summaries match your filters</h3>
          <button
            onClick={resetFilters}
            className="mt-3 text-xs text-gray-500 underline underline-offset-2 hover:text-gray-700"
          >
            Clear filters
          </button>
        </div>
      ) : (

      /* ── Summary cards ─────────────────────────────────────────────────── */
        <div className="space-y-3">
          {filteredSummaries.map((summary) => {
            const newFilings = stalenessMap.get(summary.docket_number) ?? 0;
            return (
              <div
                key={summary.id}
                role="button"
                tabIndex={0}
                aria-label={`Open ${summary.docket_number} summary`}
                className="group rounded-xl border border-gray-200 bg-white p-4 transition-shadow hover:shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 cursor-pointer"
                onClick={() => handleViewSummary(summary)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleViewSummary(summary);
                  }
                }}
              >
                <div className="flex items-start justify-between gap-4">

                  {/* Left: content */}
                  <div className="min-w-0 flex-1">

                    {/* Row 1: docket + badges */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/proceedings/${encodeURIComponent(summary.docket_number)}`);
                        }}
                        className="text-sm font-semibold text-gray-900 hover:text-gray-700 transition-colors"
                      >
                        {summary.docket_number}
                      </button>

                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${getSummaryTypeColor(summary.summary_type)}`}>
                        {summary.summary_type.replace('-', ' ')}
                      </span>

                      {(() => {
                        const label = formatFilingTypeLabel(summary.filing_type, (summary as any).period_type);
                        return label ? (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${getFilingTypeColor(summary.filing_type || '')}`}>
                            {label}
                          </span>
                        ) : null;
                      })()}

                      {newFilings > 0 && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                          ⚠ {newFilings} new filing{newFilings !== 1 ? 's' : ''} since generated
                        </span>
                      )}
                    </div>

                    {/* Row 2: topics */}
                    {summary.topics.length > 0 && (
                      <p className="text-xs text-gray-600 line-clamp-1 mb-1.5">
                        {formatTopics(summary.topics)}
                      </p>
                    )}

                    {/* Row 3: meta */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-400">
                      <span className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        {summary.document_count} doc{summary.document_count !== 1 ? 's' : ''}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDate(summary.created_at)}
                      </span>
                      {summary.file_size && (
                        <span>{Math.round(summary.file_size / 1024)} KB</span>
                      )}
                    </div>
                  </div>

                  {/* Right: actions — visible on hover */}
                  <div className="flex shrink-0 items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">

                    {/* View */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleViewSummary(summary); }}
                      className="flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                      title="View summary"
                    >
                      <Eye className="h-3 w-3" />
                      View
                    </button>

                    {/* Download */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDownloadSummary(summary); }}
                      className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                      title="Download"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>

                    {/* Share */}
                    <div className="relative" ref={shareOpen === summary.id ? sharePopoverRef : undefined}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          shareOpen === summary.id ? setShareOpen(null) : openSharePopover(summary.id);
                        }}
                        className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                        title="Share to workspace"
                      >
                        <Share2 className="h-3.5 w-3.5" />
                      </button>

                      {shareOpen === summary.id && (
                        <div role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (e.currentTarget as HTMLElement).click(); } }}
                          className="absolute right-0 top-full mt-1.5 z-30 w-64 rounded-xl border border-gray-200 bg-white p-3 shadow-lg"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {shareSuccess === summary.id ? (
                            <div className="flex items-center gap-2 py-1 text-sm font-medium text-emerald-700">
                              <Check className="h-4 w-4" />
                              Shared successfully!
                            </div>
                          ) : (
                            <>
                              <p className="mb-2.5 text-xs font-semibold text-gray-800">Share to workspace</p>
                              {!workspacesLoaded ? (
                                <p className="py-2 text-xs text-gray-400">Loading workspaces…</p>
                              ) : workspaces.length === 0 ? (
                                <p className="py-2 text-xs text-gray-500">
                                  No workspaces yet.{' '}
                                  <button
                                    onClick={() => navigate('/workspaces')}
                                    className="font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
                                  >
                                    Create one
                                  </button>
                                </p>
                              ) : (
                                <>
                                  <select
                                    value={selectedWorkspaceId}
                                    onChange={(e) => setSelectedWorkspaceId(e.target.value)}
                                    className="mb-2 w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-800 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
                                  >
                                    <option value="">Select a workspace…</option>
                                    {workspaces.map(ws => (
                                      <option key={ws.id} value={ws.id}>{ws.name}</option>
                                    ))}
                                  </select>
                                  <input
                                    type="text"
                                    placeholder="Add a note (optional)"
                                    value={shareNotes}
                                    onChange={(e) => setShareNotes(e.target.value)}
                                    className="mb-2 w-full rounded-lg border border-gray-200 px-2.5 py-1.5 text-xs text-gray-800 placeholder-gray-400 focus:border-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-300"
                                  />
                                  <button
                                    onClick={() => handleShare(summary.id)}
                                    disabled={!selectedWorkspaceId || sharing}
                                    className="w-full rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50 transition-colors"
                                  >
                                    {sharing ? 'Sharing…' : 'Share summary'}
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Delete */}
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteSummary(summary.id); }}
                      className="flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-400 hover:bg-red-50 hover:border-red-200 hover:text-red-500 transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
